import * as React from 'react';

import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { useIntl } from 'react-intl';

import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/components/sidePanel/SidePanelBody';
import payStationsMessages from 'src/features/payStations/utils/messages';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/utils/messages';

import type { PayStationLog } from 'src/features/payStations/types';

type LogDetailProps = {
    payStationLog?: PayStationLog;
};

export default function LogDetail({ payStationLog }: LogDetailProps) {
    const { formatMessage } = useIntl();
    const informationDetails = React.useMemo(() => {
        const data = payStationLog?.data;
        return [
            {
                title: formatMessage(payStationsMessages.identifier),
                value: data?.identifier,
            },
            {
                title: formatMessage(payStationsMessages.deviceType),
                value: data?.device,
            },
            {
                title: formatMessage(payStationsMessages.code),
                value: data?.eventCode || data?.code,
            },
            {
                title: formatMessage(payStationsMessages.description),
                value: data?.eventDescription,
            },
            {
                title: formatMessage(payStationsMessages.reason),
                value: data?.details?.results && data?.details?.results[0].reason,
            },
            {
                title: formatMessage(payStationsMessages.details),
                value: data?.details?.results && data?.details?.results[0].code,
            },
            {
                title: formatMessage(payStationsMessages.hasActivePayment),
                value: data?.activePayment ? formatMessage(generalMessages.yes) : formatMessage(generalMessages.no),
            },
        ];
    }, [payStationLog]);
    return (
        <>
            {payStationLog ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">{payStationLog.device.identifier}</DialogTitle>
                    </div>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                </DialogHeader>
            )}

            <DialogSeparator />

            <DialogBody>
                {Object.keys(payStationLog?.data || {}).length > 0 ? (
                    <div className="grid gap-2">
                        {informationDetails.map(({ title, value }) => {
                            return value ? (
                                <SidePanelBodyAttribute>
                                    <SidePanelBodyAttributeTitle>{title}</SidePanelBodyAttributeTitle>
                                    <SidePanelBodyAttributeBody control>{value}</SidePanelBodyAttributeBody>
                                </SidePanelBodyAttribute>
                            ) : null;
                        })}
                    </div>
                ) : (
                    <GeneralMessageBlock
                        className="h-full"
                        icon={faSearch}
                        title={formatMessage(payStationsMessages.noExtraInformation)}
                        subtitle={formatMessage(payStationsMessages.noExtraInformationLog)}
                    />
                )}
            </DialogBody>

            <DialogSeparator />

            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
}
